import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import swal from 'sweetalert'

import { CatchError } from 'Exceptions/exceptions'
import QrisExtConfigsService from 'Main/service/QrisExtConfigService'
import CommonListApiPaginated from 'views/components/Table/CommonListApiPaginated'
import { getRefreshToken } from 'variables/common'
import { QrisExtConfigModal } from './Modal/QrisExtConfigModal'

function QrisExtConfigsManager () {
  const navigate = useNavigate()
  const refreshToken = getRefreshToken()
  const qrisExtConfigsService = useMemo(() => new QrisExtConfigsService(), [])
  const [reloadKey, setReloadKey] = useState(0)
  const [paymentVendors, setPaymentVendors] = useState([])
  const [modalOpen, setModalOpen] = useState(false)
  const [modalIsAdd, setModalIsAdd] = useState(true)
  const [modalConfig, setModalConfig] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  const getConfigs = useCallback(() => qrisExtConfigsService.getData(), [qrisExtConfigsService])

  const handleListError = useCallback((error) => {
    CatchError(error, navigate, refreshToken)
  }, [navigate, refreshToken])

  useEffect(() => {
    qrisExtConfigsService
      .getPaymentVendors()
      .then((response) => setPaymentVendors(Array.isArray(response.data) ? response.data : []))
      .catch(() => {
        /* dropdown and vendor labels fall back safely if this fails */
      })
  }, [qrisExtConfigsService])

  const vendorName = useCallback((id) => {
    const vendor = paymentVendors.find((item) => String(item.id) === String(id))
    return vendor ? vendor.name : id
  }, [paymentVendors])

  const handleAdd = () => {
    setModalIsAdd(true)
    setModalConfig(null)
    setModalOpen(true)
  }

  const handleEdit = useCallback(async (id) => {
    try {
      const response = await qrisExtConfigsService.getById(id)
      if (!response.data || !response.data.id) {
        swal('Error', 'Qris Callback Not Found', 'error')
        return
      }
      setModalIsAdd(false)
      setModalConfig(response.data)
      setModalOpen(true)
    } catch (error) {
      const caught = CatchError(error, navigate, refreshToken)
      if (caught !== undefined) swal('Error', 'Failed to load QRIS callback config', 'error')
    }
  }, [navigate, qrisExtConfigsService, refreshToken])

  const handleModalSubmit = async (form) => {
    try {
      setSubmitting(true)
      if (modalIsAdd) await qrisExtConfigsService.create(form)
      else await qrisExtConfigsService.update(modalConfig.id, form)

      setModalOpen(false)
      await swal('Success!', modalIsAdd ? 'Qris Callback Added' : 'Qris Callback Updated', 'success')
      setReloadKey((current) => current + 1)
    } catch (error) {
      const caught = CatchError(error, navigate, refreshToken)
      if (caught !== undefined) swal('Error', 'Failed to save QRIS callback config', 'error')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = useCallback((id) => {
    swal('Delete Qris Callback', 'Are You Sure Delete This Qris Callback?', 'warning', {
      buttons: { cancel: 'Cancel', delete: { text: 'Delete', value: 'delete' } },
      dangerMode: true
    }).then(async (value) => {
      if (value !== 'delete') return

      try {
        await qrisExtConfigsService.deleteById(id)
        await swal('Success!', 'Qris Callback Deleted', 'success')
        setReloadKey((current) => current + 1)
      } catch (error) {
        const caught = CatchError(error, navigate, refreshToken)
        if (caught !== undefined) swal('Error', 'Failed to delete QRIS callback config', 'error')
      }
    })
  }, [navigate, qrisExtConfigsService, refreshToken])

  const columns = useMemo(() => [
    { header: 'ID', accessorKey: 'id' },
    { header: 'Name', accessorKey: 'name' },
    { header: 'Prefix', accessorKey: 'prefix' },
    { header: 'Callback Url', accessorKey: 'callback_url' },
    {
      header: 'Vendor Gateway',
      accessorKey: 'payment_vendors_id',
      cell: ({ getValue }) => vendorName(getValue())
    },
    {
      header: 'Enabled',
      accessorKey: 'enabled',
      cell: ({ getValue }) => getValue()
        ? <span className="status-success">Enabled</span>
        : <span className="status-danger">Disabled</span>
    },
    {
      header: 'Action',
      id: 'actions',
      enableSorting: false,
      filterable: false,
      exportable: false,
      cell: ({ row }) => (
        <div className="d-flex gap-1">
          <button type="button" onClick={() => handleEdit(row.original.id)} className="btn btn-primary" title="Edit Qris Callback">
            <i className="fa-solid fa-pen-to-square" />
          </button>
          <button type="button" onClick={() => handleDelete(row.original.id)} className="btn btn-danger" title="Delete Qris Callback">
            <i className="fa-solid fa-trash-can" />
          </button>
        </div>
      )
    }
  ], [handleDelete, handleEdit, vendorName])

  const menu = (
    <div className="text-center">
      <button type="button" onClick={handleAdd} className="btn btn-success" title="Tambah Qris Callback">
        <i className="fa-solid fa-pen-to-square" />&nbsp;&nbsp; Tambah Qris Callback
      </button>
    </div>
  )

  return (
    <div className="page-content">
      <section>
        <CommonListApiPaginated
          header="List Qris Callback"
          subtitle=""
          columns={columns}
          getData={getConfigs}
          menu={menu}
          reloadKey={reloadKey}
          loadingLabel="Loading QRIS callback configs..."
          emptyLabel="No QRIS callback configs found"
          onError={handleListError}
        />
      </section>

      <QrisExtConfigModal
        show={modalOpen}
        isAdd={modalIsAdd}
        config={modalConfig}
        vendors={paymentVendors}
        submitting={submitting}
        onClose={() => setModalOpen(false)}
        onSubmit={handleModalSubmit}
      />
    </div>
  )
}

export default QrisExtConfigsManager
